import {useCallback} from 'react';

import NiceModal, {useModal} from '@ebay/nice-modal-react';
import {Observer} from 'mobx-react';

import {useI18n} from '@yourcoach/i18n';

import type {Country} from '../../../../utils/countries';
import type {GetComponentProps} from '../../../../utils/utility-types';
import type {CountriesListItem} from '../../containers/CountriesListItem';

import SearchIcon from '../../../../assets/icons/search.svg';
import {Modal} from '../../../../components/Modal';
import {NoResultsView} from '../../../../components/NoResultsView';
import {TextInput} from '../../../../uikit/TextInput';
import {useMobxStore} from '../../../../utils/store';
import {SelectCountryStore} from '../../stores/SelectCountry';
import * as S from './styles';

export const SelectCountryModal = NiceModal.create(() => {
  const {t} = useI18n();
  const modal = useModal();
  const store = useMobxStore(() => new SelectCountryStore());

  const onRequestClose = useCallback(() => {
    modal.resolve();
    modal.hide();
  }, [modal]);

  const onCountryClick: GetComponentProps<typeof CountriesListItem>['onClick'] =
    useCallback(
      country => {
        modal.resolve(country);
        modal.hide();
      },
      [modal],
    );

  const renderSearch = () => (
    <Observer>
      {() => (
        <S.SearchTextInputContainer>
          <TextInput
            appearance="search"
            onChange={store.onSearchQueryChange}
            placeholder={store.searchPlaceholder}
          />
        </S.SearchTextInputContainer>
      )}
    </Observer>
  );

  return (
    <Observer>
      {() => (
        <Modal
          afterTitleSlot={renderSearch}
          isFullWidth
          isOpen={modal.visible}
          maxWidth="500px"
          onAfterClose={modal.remove}
          onRequestClose={onRequestClose}
          title={store.title}
        >
          <S.ListContainer>
            {store.countries.length ? (
              <>
                {store.countries.map(country => (
                  <S.ListItem
                    country={country}
                    key={country.iso2}
                    onClick={onCountryClick}
                  />
                ))}
              </>
            ) : (
              <NoResultsView
                icon={SearchIcon}
                iconColor="icon.gray"
                iconSize={72}
                text={t('shared:nothing_found')}
              />
            )}
          </S.ListContainer>
        </Modal>
      )}
    </Observer>
  );
});

export namespace SelectCountryModal {
  export type Resolve = Country | undefined;
}
